import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal, Select } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { EnterpriseLocation, EnterpriseMember } from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import RevertIcon from 'icons/Revert.icon';
import TrashIcon from 'icons/Trash.icon';
import { getUserFullName } from 'utils';

import './DeleteMemberLocationModal.styles.scss';

export type DeleteLocation = {
  deleteEnterpriseMemberIds?: string[];
  updateEnterpriseMembers?: {
    memberId: string;
    enterpriseLocationId: string;
  }[];
};

const INITIAL_DATA_FOR_SUBMIT: DeleteLocation = {
  deleteEnterpriseMemberIds: [],
  updateEnterpriseMembers: [],
};

type DeleteMemberLocationModalProps = {
  className?: string;
  enterpriseLocationId: string;
  enterpriseMembersWithLocation: EnterpriseMember[];
  memberLocations: EnterpriseLocation[];
  updateMemberLocations: (
    updatedMemberLocations: EnterpriseLocation[],
    shouldRefetch?: boolean,
  ) => void;
} & ModalProps;

const DeleteMemberLocationModal: React.ForwardRefRenderFunction<
  ModalRef,
  DeleteMemberLocationModalProps
> = (props, ref) => {
  const {
    className,
    enterpriseLocationId,
    memberLocations,
    enterpriseMembersWithLocation = [],
    updateMemberLocations,
    onClose,
    ...rest
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [dataForSubmit, setDataForSubmit] = useState<DeleteLocation>(
    INITIAL_DATA_FOR_SUBMIT,
  );

  const { popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('delete-member-location-modal', className);

  const { t } = useTranslation();

  const validForSubmit = useMemo(
    () =>
      enterpriseMembersWithLocation.every(({ _id }) => {
        const { deleteEnterpriseMemberIds, updateEnterpriseMembers } =
          dataForSubmit;

        return (
          deleteEnterpriseMemberIds.includes(_id) ||
          updateEnterpriseMembers.some(({ memberId }) => memberId === _id)
        );
      }),
    [dataForSubmit, enterpriseMembersWithLocation],
  );

  const selectOptions = useMemo(
    () =>
      memberLocations
        .filter(({ _id }) => _id !== enterpriseLocationId)
        .map(({ _id, name }) => ({ value: _id, label: name })),
    [enterpriseLocationId, memberLocations],
  );

  const getSelectValue = useCallback(
    (mId: string) =>
      dataForSubmit.updateEnterpriseMembers.find(
        ({ memberId }) => memberId === mId,
      )?.enterpriseLocationId || null,
    [dataForSubmit.updateEnterpriseMembers],
  );

  const getIsDeleted = useCallback(
    (mId: string) =>
      dataForSubmit.deleteEnterpriseMemberIds.some(
        (memberId) => memberId === mId,
      ),
    [dataForSubmit.deleteEnterpriseMemberIds],
  );

  const onSelectChange = useCallback((memberId: string, locationId: string) => {
    setDataForSubmit(
      ({ deleteEnterpriseMemberIds, updateEnterpriseMembers }) => ({
        deleteEnterpriseMemberIds: deleteEnterpriseMemberIds.filter(
          (id) => id !== memberId,
        ),
        updateEnterpriseMembers: [
          ...updateEnterpriseMembers.filter(
            ({ memberId: id }) => id !== memberId,
          ),
          { memberId, enterpriseLocationId: locationId },
        ],
      }),
    );
  }, []);

  const onRemove = useCallback((memberId: string) => {
    setDataForSubmit(
      ({ deleteEnterpriseMemberIds, updateEnterpriseMembers }) => ({
        deleteEnterpriseMemberIds: deleteEnterpriseMemberIds.some(
          (id) => id === memberId,
        )
          ? deleteEnterpriseMemberIds.filter((id) => id !== memberId)
          : [...deleteEnterpriseMemberIds, memberId],
        updateEnterpriseMembers: [
          ...updateEnterpriseMembers.filter(
            ({ memberId: id }) => id !== memberId,
          ),
        ],
      }),
    );
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.enterprises.deleteMemberLocation(
        enterpriseLocationId,
        dataForSubmit,
      );
      updateMemberLocations(data);
      modal?.close();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    dataForSubmit,
    enterpriseLocationId,
    modal,
    popServerError,
    updateMemberLocations,
  ]);

  const onCustomClose = useCallback(() => {
    setDataForSubmit(INITIAL_DATA_FOR_SUBMIT);
    onClose?.();
  }, [onClose]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('connectMembersAndLocation')}
      onClose={onCustomClose}
      {...rest}
    >
      {isLoading && <Loader />}

      <p className="description">{t('connectMembersAndLocationDescription')}</p>

      <div className="members-section">
        {enterpriseMembersWithLocation.map(({ _id, firstName, lastName }) => {
          const isDeleted = getIsDeleted(_id);

          return (
            <div key={_id}>
              <p
                className={classNames({
                  removed: isDeleted,
                })}
              >
                {getUserFullName(firstName, lastName)}
              </p>

              <Select
                renderAsPortal
                portalClassName="select-value-options"
                value={getSelectValue(_id)}
                placeholder={t('choose')}
                options={selectOptions}
                onChange={(e: any) => onSelectChange(_id, e.value)}
              />

              <Button
                variant="icon"
                icon={isDeleted ? RevertIcon : TrashIcon}
                className={classNames({
                  'warning-action-button': !isDeleted,
                })}
                onClick={() => onRemove(_id)}
              />
            </div>
          );
        })}
      </div>

      <div className="actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>

        <Button disabled={isLoading || !validForSubmit} onClick={onSubmit}>
          {t('confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(DeleteMemberLocationModal);
